import { Item, Root, Trigger } from '@radix-ui/react-accordion';
import { type FC, useId, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { AccordionItemConfigProps } from 'molecules/Accordion/config';
import CollapsiblePanel from 'molecules/CollapsiblePanel';
import Icon from 'molecules/Icon';
import RichText from 'molecules/RichText';

import { useScrollToAccordionItem } from 'components/Tables/PricingV2/hooks/useScrollToAccordionItem';

import { useIsDesktop } from 'utils/hooks/useIsDesktop';

interface RadixAccordionProps {
  accordionItems: AccordionItemConfigProps[];
  className?: string;
  isBulletStandard?: boolean;
}

const AccordionItem: FC<RadixAccordionProps> = ({ accordionItems, className, isBulletStandard = false }) => {
  const [openValue, setOpenValue] = useState<string>('0');
  const idPrefix = useId();
  const isDesktop = useIsDesktop();
  const refs = useScrollToAccordionItem(openValue, isDesktop ? 92 : 80);

  return (
    <Root
      type="single"
      collapsible
      value={openValue}
      onValueChange={val => setOpenValue(val ?? '')}
      className={twJoin('m-auto grid w-full max-w-[905px] gap-4', className)}
    >
      {accordionItems.map((item, idx) => {
        const value = String(idx);
        const isOpen = openValue === value;
        const triggerId = `${idPrefix}-trigger-${value}`;
        const panelId = `${idPrefix}-panel-${value}`;

        return (
          <Item
            key={item.label}
            value={value}
            className={twJoin([
              'flex flex-col justify-between rounded-none px-8 py-5 data-[state=open]:py-8',
              'bg-transparent shadow-none transition-[background-color,_border-radius,_padding] duration-300',
              'data-[state=closed]:border-b data-[state=closed]:border-primary-400',
              'data-[state=open]:rounded-lg data-[state=open]:bg-white',
              'dark:data-[state=closed]:border-white',
            ])}
          >
            <div
              ref={el => {
                refs.current[value] = el;
              }}
            />

            <Trigger
              id={triggerId}
              aria-controls={panelId}
              className={twJoin(
                'flex justify-between gap-6 text-left text-lg font-semibold bleed-5 sm:text-xl [&_svg]:data-[state=open]:rotate-180]',
                'text-gray-900 dark:text-white',
                '[.connector-faq_&]:data-[state=open]:!text-gray-900',
              )}
            >
              {item.label}
              <Icon icon="chevron-down" className="min-w-6 text-primary-900 transition-transform duration-300" />
            </Trigger>

            <CollapsiblePanel id={panelId} labelledBy={triggerId} isOpen={isOpen}>
              {item.description && (
                <RichText
                  className="overflow-hidden pt-2 text-md text-gray-1100 duration-300 sm:text-lg"
                  text={item.description}
                  isBulletStandard={isBulletStandard}
                />
              )}
            </CollapsiblePanel>
          </Item>
        );
      })}
    </Root>
  );
};

export default AccordionItem;
